import { format } from 'date-fns';
import { 
  CheckCircle, 
  Heart, 
  Stethoscope, 
  ClipboardText, 
  Pill,
  FileText,
  Envelope,
  UserCircle,
  ArrowRight,
  CalendarBlank
} from '@phosphor-icons/react';
import { Calendar } from '../../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';

export default function AVSModal({
  isOpen,
  onClose,
  onSendAVS,
  onSendToCMA,
  encounter,
  intake,
  selectedDiagnoses,
  treatmentPlan,
  prescriptions,
  selectedPharmacy,
  followUpDate,
  setFollowUpDate,
  followUpInstructions,
  setFollowUpInstructions,
  avsLoading,
  avsSent,
  avsSummaryId,
  avsError
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 m-4 my-8" data-testid="avs-modal">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" weight="fill" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900">Encounter Completed</h3>
          <p className="text-slate-500 mt-2">
            Review and send the After Visit Summary to the patient
          </p>
        </div>

        {/* Error Message */}
        {avsError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4" data-testid="avs-error">
            <p className="text-red-700 text-sm">{avsError}</p>
          </div>
        )}

        {/* Comprehensive Summary Preview */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6 max-h-96 overflow-y-auto">
          <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2 text-lg">
            <FileText size={20} /> After Visit Summary
          </h4>
          
          <div className="space-y-4 text-sm">
            {/* Patient & Visit Info */}
            <div className="grid grid-cols-2 gap-4 pb-3 border-b border-slate-200">
              <div>
                <span className="text-slate-500 block">Patient</span>
                <span className="font-medium">{encounter?.patient_name}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Visit Date</span>
                <span className="font-medium">{new Date().toLocaleDateString()}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Visit Type</span>
                <span className="font-medium capitalize">{encounter?.encounter_type?.replace(/_/g, ' ') || 'Office Visit'}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Reason for Visit</span>
                <span className="font-medium">{intake?.chief_complaint || 'General visit'}</span>
              </div>
            </div>

            {/* Vitals */}
            {intake?.vital_signs && (
              <div className="pb-3 border-b border-slate-200" data-testid="avs-vitals">
                <h5 className="font-medium text-slate-900 mb-2 flex items-center gap-1">
                  <Heart size={16} className="text-red-500" /> Vitals
                </h5>
                <div className="grid grid-cols-3 gap-2 text-slate-700">
                  {intake.vital_signs.blood_pressure && (
                    <span>BP: {intake.vital_signs.blood_pressure}</span>
                  )}
                  {intake.vital_signs.heart_rate && (
                    <span>HR: {intake.vital_signs.heart_rate} bpm</span>
                  )}
                  {intake.vital_signs.temperature && (
                    <span>Temp: {intake.vital_signs.temperature}°F</span>
                  )}
                  {intake.vital_signs.respiratory_rate && (
                    <span>Resp: {intake.vital_signs.respiratory_rate}/min</span>
                  )}
                  {intake.vital_signs.oxygen_saturation && (
                    <span>O2 Sat: {intake.vital_signs.oxygen_saturation}%</span>
                  )}
                  {intake.vital_signs.weight && (
                    <span>Weight: {intake.vital_signs.weight} lbs</span>
                  )}
                  {intake.vital_signs.height && (
                    <span>Height: {intake.vital_signs.height}</span>
                  )}
                  {intake.vital_signs.bmi && (
                    <span>BMI: {intake.vital_signs.bmi}</span>
                  )}
                </div>
              </div>
            )}

            {/* Assessment/Diagnosis */}
            <div className="pb-3 border-b border-slate-200" data-testid="avs-assessment">
              <h5 className="font-medium text-slate-900 mb-2 flex items-center gap-1">
                <Stethoscope size={16} className="text-teal-600" /> Assessment (Diagnosis)
              </h5>
              {selectedDiagnoses.length > 0 ? (
                <ul className="space-y-1">
                  {selectedDiagnoses.map((d, idx) => (
                    <li key={idx} className="text-slate-700">
                      • <span className="font-mono text-teal-600">{d.code}</span>: {d.description}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 italic">No diagnosis recorded</p>
              )}
            </div>

            {/* Treatment Plan */}
            <div className="pb-3 border-b border-slate-200" data-testid="avs-treatment-plan">
              <h5 className="font-medium text-slate-900 mb-2 flex items-center gap-1">
                <ClipboardText size={16} className="text-blue-600" /> Treatment Plan
              </h5>
              {treatmentPlan ? (
                <p className="text-slate-700 whitespace-pre-wrap">{treatmentPlan}</p>
              ) : (
                <p className="text-slate-500 italic">No treatment plan recorded</p>
              )}
            </div>

            {/* Medications Prescribed */}
            <div className="pb-3 border-b border-slate-200" data-testid="avs-medications">
              <h5 className="font-medium text-slate-900 mb-2 flex items-center gap-1">
                <Pill size={16} className="text-purple-600" /> Medications Prescribed
              </h5>
              {prescriptions.length > 0 ? (
                <ul className="space-y-2">
                  {prescriptions.map((rx, idx) => (
                    <li key={idx} className="text-slate-700 bg-white p-2 rounded border">
                      <p className="font-medium">{rx.medication?.name} {rx.medication?.strength}</p>
                      <p className="text-slate-600 text-xs">
                        {rx.dosage} - {rx.frequency} {rx.duration && `for ${rx.duration}`}
                      </p>
                      {rx.instructions && (
                        <p className="text-slate-500 text-xs mt-1">{rx.instructions}</p>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 italic">No medications prescribed this visit</p>
              )}
              {selectedPharmacy && (
                <p className="text-slate-600 text-xs mt-2">
                  Pharmacy: {selectedPharmacy.name} - {selectedPharmacy.address}
                </p>
              )}
            </div>

            {/* Follow-up Appointment */}
            <div data-testid="avs-followup">
              <h5 className="font-medium text-slate-900 mb-2">Follow-up Appointment</h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-clinical text-xs">Follow-up Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="followup-date"
                        className={`input-clinical text-sm w-full text-left flex items-center gap-2 ${
                          !followUpDate && "text-slate-400"
                        }`}
                      >
                        <CalendarBlank size={16} />
                        {followUpDate ? (
                          format(new Date(followUpDate), "MMM d, yyyy")
                        ) : (
                          <span>Select date</span>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={followUpDate ? new Date(followUpDate) : undefined}
                        onSelect={(date) => setFollowUpDate(date ? format(date, 'yyyy-MM-dd') : '')}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="label-clinical text-xs">Instructions</label>
                  <select
                    value={followUpInstructions}
                    onChange={(e) => setFollowUpInstructions(e.target.value)}
                    className="input-clinical text-sm"
                    data-testid="followup-instructions"
                  >
                    <option value="">Select timeframe...</option>
                    <option value="Return in 1 week">Return in 1 week</option>
                    <option value="Return in 2 weeks">Return in 2 weeks</option>
                    <option value="Return in 1 month">Return in 1 month</option>
                    <option value="Return in 3 months">Return in 3 months</option>
                    <option value="Return in 6 months">Return in 6 months</option>
                    <option value="Return as needed">Return as needed</option>
                    <option value="Annual visit">Annual visit</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {avsSent ? (
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <CheckCircle size={24} className="text-green-600 mx-auto mb-2" weight="fill" />
              <p className="text-green-700 font-medium">After Visit Summary Sent!</p>
              <p className="text-green-600 text-sm mt-1">
                A copy has been saved to the patient&apos;s records
              </p>
              {avsSummaryId && (
                <p className="text-green-600 text-xs mt-2">Summary ID: {avsSummaryId}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-full btn-teal"
              data-testid="avs-done-btn"
            >
              Done - Return to Dashboard
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={onSendAVS}
              disabled={avsLoading}
              className="w-full btn-teal flex items-center justify-center gap-2"
              data-testid="send-avs-btn"
            >
              {avsLoading ? (
                <>
                  <div className="spinner" /> Sending...
                </>
              ) : (
                <>
                  <Envelope size={18} />
                  Email After Visit Summary to Patient
                </>
              )}
            </button>
            
            {/* CMA Checkout Option */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-slate-500">or</span>
              </div>
            </div>
            
            <button
              onClick={onSendToCMA}
              className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
              data-testid="send-to-cma-btn"
            >
              <UserCircle size={18} />
              Send to CMA Checkout
              <ArrowRight size={16} className="ml-1" />
            </button>
            <p className="text-xs text-slate-500 text-center">
              CMA will handle scheduling, pharmacy verification, and finalize AVS
            </p>
            
            <button
              onClick={onClose}
              className="w-full btn-outline"
              data-testid="skip-avs-btn"
            >
              Skip - Complete Without Sending
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
